// Pannello referenti per una sede — lista + CRUD tramite modal
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Pencil, Trash2, Star } from 'lucide-react'
import { Modal } from '../../../components/ui/Modal'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Badge } from '../../../components/ui/Badge'
import api from '../../../lib/api'

interface SiteContact {
  id: string
  name: string
  role?: string | null
  phone?: string | null
  email?: string | null
  isPrimary?: boolean
  notes?: string | null
}

const contactSchema = z.object({
  name: z.string().min(1, 'Nome obbligatorio'),
  role: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Email non valida').optional().or(z.literal('')),
  isPrimary: z.boolean().optional(),
  notes: z.string().optional(),
})

type ContactFormData = z.infer<typeof contactSchema>

interface ContactFormModalProps {
  siteId: string
  contact?: SiteContact | null
  open: boolean
  onClose: () => void
}

function ContactFormModal({ siteId, contact, open, onClose }: ContactFormModalProps) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(contact?.id)

  const { register, handleSubmit, formState: { errors } } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: contact?.name ?? '',
      role: contact?.role ?? '',
      phone: contact?.phone ?? '',
      email: contact?.email ?? '',
      isPrimary: contact?.isPrimary ?? false,
      notes: contact?.notes ?? '',
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      if (isEdit && contact?.id) {
        await api.put(`/sites/${siteId}/contacts/${contact.id}`, data)
      } else {
        await api.post(`/sites/${siteId}/contacts`, data)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['site-contacts', siteId] })
      onClose()
    },
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Modifica referente' : 'Nuovo referente'}
      size="md"
    >
      <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-3">
        <Input label="Nome *" {...register('name')} error={errors.name?.message} placeholder="Mario Rossi" />
        <Input label="Ruolo" {...register('role')} placeholder="Responsabile tecnico" />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Telefono" {...register('phone')} placeholder="+39 348 0000000" />
          <Input label="Email" type="email" {...register('email')} error={errors.email?.message} placeholder="m.rossi@azienda.it" />
        </div>
        <div className="flex items-center gap-2">
          <input id="isPrimary" type="checkbox" {...register('isPrimary')} className="h-4 w-4 rounded border-[var(--fc-border)] accent-[var(--fc-primary)]" />
          <label htmlFor="isPrimary" className="text-sm text-[var(--fc-foreground)]">Referente principale</label>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-[var(--fc-foreground)]">Note</label>
          <textarea
            {...register('notes')}
            rows={2}
            className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
          />
        </div>
        {mutation.isError && (
          <p className="text-sm text-[var(--fc-danger)]">Errore durante il salvataggio.</p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>Annulla</Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Salvataggio...' : isEdit ? 'Salva' : 'Aggiungi'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

interface SiteContactsPanelProps {
  siteId: string
  siteName: string
}

/**
 * Pannello che mostra i referenti di una sede con possibilità di aggiungere,
 * modificare ed eliminare.
 */
export function SiteContactsPanel({ siteId, siteName }: SiteContactsPanelProps) {
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingContact, setEditingContact] = useState<SiteContact | null>(null)

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['site-contacts', siteId],
    queryFn: async () => {
      const res = await api.get<SiteContact[]>(`/sites/${siteId}/contacts`)
      return res.data ?? []
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (contactId: string) => {
      await api.delete(`/sites/${siteId}/contacts/${contactId}`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['site-contacts', siteId] })
    },
  })

  const openAdd = () => {
    setEditingContact(null)
    setModalOpen(true)
  }

  const openEdit = (contact: SiteContact) => {
    setEditingContact(contact)
    setModalOpen(true)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--fc-foreground-muted)]">
          Referenti — {siteName}
        </p>
        <Button size="xs" variant="secondary" leftIcon={<Plus className="h-3 w-3" />} onClick={openAdd}>
          Aggiungi
        </Button>
      </div>

      {isLoading && (
        <p className="text-xs text-[var(--fc-foreground-muted)]">Caricamento referenti...</p>
      )}

      {!isLoading && contacts.length === 0 && (
        <p className="text-xs text-[var(--fc-foreground-muted)]">Nessun referente registrato.</p>
      )}

      {contacts.map((contact) => (
        <div
          key={contact.id}
          className="flex items-start justify-between rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2"
        >
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-[var(--fc-foreground)]">{contact.name}</span>
              {contact.isPrimary && (
                <Badge variant="default">
                  <Star className="h-2.5 w-2.5 mr-0.5" />
                  Principale
                </Badge>
              )}
            </div>
            {contact.role && <p className="text-xs text-[var(--fc-foreground-muted)]">{contact.role}</p>}
            <div className="flex gap-3 text-xs text-[var(--fc-foreground-muted)]">
              {contact.phone && <span>{contact.phone}</span>}
              {contact.email && <span>{contact.email}</span>}
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button
              size="icon-sm"
              variant="ghost"
              onClick={() => openEdit(contact)}
              aria-label="Modifica referente"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon-sm"
              variant="ghost"
              onClick={() => deleteMutation.mutate(contact.id)}
              aria-label="Elimina referente"
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-3.5 w-3.5 text-[var(--fc-danger)]" />
            </Button>
          </div>
        </div>
      ))}

      <ContactFormModal
        siteId={siteId}
        contact={editingContact}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}
